import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { imageGenerationService } from '../services/imageGenerationService';
import { useImageSettingsStore } from '../store/imageSettingsStore';
import { useImageHistory } from '../hooks/useImageHistory';
import { imageCacheService } from '../services/imageCacheService';
import { GeneratedImage, ImageGenerationRequest } from '../types/imageGeneration';
import ImageFieldPanel from '../components/image/ImageFieldPanel';
import ImageModal from '../components/image/ImageModal';
import ImageSettingsBar from '../components/image/ImageSettingsBar';
import ImageHistoryDisplay from '../components/image/ImageHistoryDisplay';
import LazyImage from '../components/image/LazyImage';
import styles from '../styles/pages/ImageGenerationPage.module.css';

const ImageGenerationPage: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<{
    right?: number;
    bottom?: number;
  }>({});
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [notice, setNotice] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{
    code?: string;
    canRetry?: boolean;
    showModelSelector?: boolean;
  } | null>(null);

  // Set page title and metadata
  useEffect(() => {
    document.title = 'Image Generation - Swarmind';
    
    // Set meta description for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Generate AI-powered images using text prompts with Swarmind\'s advanced image generation models.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Generate AI-powered images using text prompts with Swarmind\'s advanced image generation models.';
      document.head.appendChild(meta);
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Swarmind';
    };
  }, []);

  // Settings from store - memoized selectors for performance
  const selectedModelId = useImageSettingsStore(useCallback(s => s.selectedModelId, []));
  const imageSize = useImageSettingsStore(useCallback(s => s.imageSize, []));
  const steps = useImageSettingsStore(useCallback(s => s.steps, []));
  const quantity = useImageSettingsStore(useCallback(s => s.quantity, []));
  const seed = useImageSettingsStore(useCallback(s => s.seed, []));
  const setSelectedModelId = useImageSettingsStore(useCallback(s => s.setSelectedModelId, []));

  // History management
  const { saveGeneration } = useImageHistory();

  const location = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [generatedImages, scrollToBottom]);

  // Ensure we are scrolled to the bottom on first load as well
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  // Preload generated images for better performance
  useEffect(() => {
    if (generatedImages.length > 0) {
      const imageUrls = generatedImages.map(img => img.url);
      imageCacheService.preloadImages(imageUrls);
    }
  }, [generatedImages]);

  // Apply selected model from navigation state when arriving from models gallery
  useEffect(() => {
    const state = location.state as { selectedModel?: string } | null;
    if (state?.selectedModel) {
      setSelectedModelId(state.selectedModel);
    }
  }, [location.state, setSelectedModelId]);

  const handleGenerateImages = async () => {
    if (!prompt.trim() || isLoading) return;

    setNotice(null);
    setIsLoading(true);
    setGeneratedImages([]);

    try {
      // Validate uploaded image if present
      if (uploadedImage) {
        imageGenerationService.validateImageFile(uploadedImage);
      }

      // Validate request parameters
      const request: ImageGenerationRequest = {
        model: await imageGenerationService.resolveApiModelName(selectedModelId),
        prompt: prompt.trim(),
        steps,
        n: quantity,
        size: imageSize,
        ...(uploadedImage && { image: uploadedImage }),
      };

      imageGenerationService.validateRequest(request);

      console.log('🎨 Generating images with request:', {
        ...request,
        image: uploadedImage ? '[File object]' : undefined
      });

      const response = await imageGenerationService.generateImages(request);
      
      if (response.data && response.data.length > 0) {
        setGeneratedImages(response.data);
        // Save to history
        await saveGeneration(prompt, response.data, {
          selectedModelId,
          imageSize,
          steps,
          quantity,
          seed,
        });
        setPrompt('');
      } else {
        throw new Error('No images were generated');
      }
    } catch (e: any) {
      const errorInfo = handleGenerationError(e);
      setNotice(errorInfo.message);
      setErrorDetails(errorInfo.details);
      console.error('❌ Image generation failed:', e);
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        const input = document.getElementById('prompt-input') as HTMLInputElement | null;
        input?.focus();
      }, 0);
    }
  };

  /**
   * Handle and categorize generation errors for user-friendly display
   */
  const handleGenerationError = (error: any): {
    message: string;
    details: { code?: string; canRetry?: boolean; showModelSelector?: boolean };
  } => {
    let message = 'Error generating images';
    let canRetry = false;
    let showModelSelector = false;
    const code = error?.code;

    switch (code) {
      case 'INSUFFICIENT_BALANCE':
        message = 'Insufficient balance. Please top up your account to continue generating images.';
        break;
      
      case 'MODEL_NOT_FOUND':
        message = 'The selected model is not available. Please choose a different model in settings.';
        showModelSelector = true;
        break;
      
      case 'NETWORK_ERROR':
        message = 'Network connection failed. Please check your internet connection and try again.';
        canRetry = true;
        break;
      
      case 'SERVER_ERROR':
        message = 'Server error occurred. Please try again in a few moments.';
        canRetry = true;
        break;
      
      case 'RATE_LIMITED':
        message = 'Too many requests. Please wait a moment before trying again.';
        canRetry = true;
        break;
      
      case 'FILE_VALIDATION_ERROR':
      case 'FILE_PROCESSING_ERROR':
        message = error.message || 'Invalid image file. Please select a valid JPEG, PNG, or WebP image.';
        break;
      
      case 'INVALID_REQUEST':
      case 'VALIDATION_ERROR':
        message = 'Invalid request parameters. Please check your settings and try again.';
        break;
      
      case 'INVALID_PROMPT':
        message = 'Invalid prompt. Please check your text and try again.';
        break;
      
      case 'UNAUTHORIZED':
        message = 'Authentication failed. Please connect your MetaMask wallet and try again.';
        break;
      
      case 'FORBIDDEN':
        message = 'Access denied. Please check your account permissions.';
        break;
      
      case 'EMPTY_RESPONSE':
        message = 'No images were generated. Please try again with different settings.';
        canRetry = true;
        break;
      
      case 'INVALID_RESPONSE':
        message = 'Invalid response from server. Please try again.';
        canRetry = true;
        break;
      
      default:
        // Handle legacy error messages
        if (error?.message?.includes('MetaMask wallet address is required')) {
          message = 'Please connect your MetaMask wallet to continue. Click the wallet connection button in the top bar.';
        } else if (error?.message?.includes('Invalid file type') || error?.message?.includes('File size')) {
          message = error.message;
        } else if (error?.message?.includes('Failed to read file')) {
          message = 'Failed to process the uploaded image. Please try a different file.';
        } else if (error?.message) {
          message = error.message;
          // Default to allowing retry for unknown errors
          canRetry = true;
        } else {
          message = 'An unexpected error occurred. Please try again.';
          canRetry = true;
        }
    }

    return {
      message,
      details: { code, canRetry, showModelSelector }
    };
  };

  /**
   * Retry the last generation attempt
   */
  const handleRetryGeneration = useCallback(() => {
    setNotice(null);
    setErrorDetails(null);
    handleGenerateImages();
  }, [prompt, selectedModelId, imageSize, steps, quantity, seed, uploadedImage]);

  /**
   * Clear error state
   */
  const handleClearError = useCallback(() => {
    setNotice(null);
    setErrorDetails(null);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerateImages();
    }
  };

  const handlePromptReuse = useCallback((reusedPrompt: string) => {
    setPrompt(reusedPrompt);
    // Scroll to input area
    const inputArea = document.querySelector(`.${styles.inputArea}`);
    inputArea?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleImageView = useCallback((imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setIsImageModalOpen(true);
  }, []);

  const handleImageUpload = useCallback((file: File | null) => {
    if (file) {
      try {
        imageGenerationService.validateImageFile(file);
        setUploadedImage(file);
        // Clear any previous error notices
        setNotice(null);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Invalid image file';
        setNotice(errorMessage);
        setUploadedImage(null);
        console.error('Image upload validation failed:', error);
      }
    } else {
      setUploadedImage(null);
      // Clear notice when removing image
      setNotice(null);
    }
  }, []);

  // Memoize generated images display for performance
  const memoizedGeneratedImages = useMemo(() => {
    if (generatedImages.length === 0) return null;

    return (
      <section className={styles.generatedImagesSection} aria-label="Generated images">
        <div className={styles.imagesGrid} role="group" aria-label={`${generatedImages.length} generated image${generatedImages.length === 1 ? '' : 's'}`}>
          {generatedImages.map((image, index) => (
            <LazyImage
              key={`${image.index}-${index}`}
              src={image.url}
              alt={`Generated image ${index + 1} from prompt: ${prompt}`}
              className={styles.generatedImage}
              onClick={() => handleImageView(image.url)}
              loading="eager" // Generated images should load immediately
            />
          ))}
        </div>
        {prompt && (
          <div className={styles.promptDisplay}>
            <span className={styles.promptLabel}>Prompt:</span>
            <span className={styles.promptText}>{prompt}</span>
          </div>
        )}
      </section>
    );
  }, [generatedImages, prompt, handleImageView]);

  return (
    <main className={styles.imageGenerationPage} role="main">
      {/* Bottom underlay to hide content behind input when scrolling */}
      <div className={styles.inputUnderlay} aria-hidden="true" />

      {/* Main Content Area */}
      <div className={styles.contentContainer}>
        <div className={styles.contentArea}>
          {/* Error Notice */}
          {notice && (
            <section className={styles.noticeSection} role="alert" aria-live="assertive">
              <div className={styles.errorBubble}>
                <div className={styles.errorMessage}>{notice}</div>
                <div className={styles.errorActions} role="group" aria-label="Error actions">
                  {errorDetails?.canRetry && (
                    <button 
                      className={styles.retryButton}
                      onClick={handleRetryGeneration}
                      disabled={isLoading}
                      aria-label="Retry image generation"
                    >
                      Try Again
                    </button>
                  )}
                  {errorDetails?.showModelSelector && (
                    <button 
                      className={styles.settingsButton}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const viewportHeight = window.innerHeight;
                        const bottom = Math.max(20, viewportHeight - rect.top + 12);
                        const right = Math.max(20, window.innerWidth - rect.right);
                        setSettingsAnchor({ bottom, right });
                        setIsSettingsOpen(true);
                      }}
                      aria-label="Open model selection settings"
                    >
                      Choose Model
                    </button>
                  )}
                  <button 
                    className={styles.dismissButton}
                    onClick={handleClearError}
                    aria-label="Dismiss error message"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Image History */}
          <div className={styles.historySection}>
            <ImageHistoryDisplay
              onImageView={handleImageView}
              onHistoryUpdated={scrollToBottom}
            />
          </div>

          {/* {memoizedGeneratedImages} */}
          {isLoading && (
            <section className={styles.loadingSection} aria-live="polite" aria-busy="true">
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} aria-hidden="true"></div>
                <p className={styles.loadingText}>Generating images...</p>
              </div>
            </section>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <section className={styles.inputArea} aria-label="Image generation controls">
        <ImageFieldPanel
          value={prompt}
          onChange={setPrompt}
          onGenerate={handleGenerateImages}
          onKeyDown={handleKeyDown}
          onSettingsClick={(anchorRect?: DOMRect) => {
            if (anchorRect) {
              const viewportHeight = window.innerHeight;
              const bottom = Math.max(20, viewportHeight - anchorRect.top + 12);
              const right = Math.max(20, window.innerWidth - anchorRect.right);
              setSettingsAnchor({ bottom, right });
            } else {
              setSettingsAnchor({});
            }
            setIsSettingsOpen(true);
          }}
          onImageUpload={handleImageUpload}
          uploadedImage={uploadedImage}
          disabled={isLoading}
        />
      </section>

      {/* Settings Bar */}
      <ImageSettingsBar
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        anchorRight={settingsAnchor.right}
        anchorBottom={settingsAnchor.bottom}
      />

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={selectedImageUrl}
        onClose={() => setIsImageModalOpen(false)}
      />
    </main>
  );
};

export default ImageGenerationPage;